import { useAdInterfaceContext } from './AdInterface.tsx';
import { useInGameNotificationContext } from './InGameNotificationContext.tsx';
import { useLoginSession } from './SessionContext.tsx';
import { PaywallTrigger, useSubscriptionContext } from './SubscriptionContext.tsx';
import { track } from './umami.ts';
import { getAppStoreWebLink, isCandidateForApp, runningAsNativeApp, UserCancellationError } from './utils.ts';
import * as Sentry from '@sentry/capacitor';

export function SignInCta() {
    const loginSession = useLoginSession();
    return (
        <button
            onClick={async () => {
                try {
                    await loginSession.getOrOpenSession();
                } catch (error) {
                    if (!(error instanceof UserCancellationError)) {
                        console.error('Unexpected error during login:', error);
                        Sentry.captureException(error);
                    }
                }
            }}
            className='linkish-button adjustment-text-link app-download-text'>
            Sign in to save your stats!
        </button>
    );
}

export function AppDownloadCta() {
    return (
        <a
            href={getAppStoreWebLink()}
            className='adjustment-text-link app-download-text'
            onClick={() => {
                track('app_store_link_clicked_victory_modal');
            }}>
            Check out the Triplit app!
        </a>
    );
}

export function VictoryModalCta(victoryModalOpenCount: number) {
    const loginSession = useLoginSession();
    const subscriptionContext = useSubscriptionContext();
    const inGameNotifications = useInGameNotificationContext();
    const { pro } = useSubscriptionContext();
    const adInterface = useAdInterfaceContext();

    function upgradeToProCta() {
        return (
            <button
                className='linkish-button adjustment-text-link app-download-text'
                onClick={() => {
                    void subscriptionContext.ensureProLevel(PaywallTrigger.USER_INITIATED_UPGRADE).catch((error) => {
                        if (error instanceof UserCancellationError) {
                            return;
                        }
                        inGameNotifications.displayNotification(
                            'There was a problem upgrading you to Pro. Please try again later.',
                            3000
                        );
                        console.error('Error during Pro upgrade from victory modal:', error);
                        Sentry.captureException(error);
                    });
                }}>
                Skip the ads. Keep the fun.
                <br />
                Try Triplit Pro.
            </button>
        );
    }

    function ctaForAppCandidates() {
        return !loginSession?.session ? (
            // Alternate between app download and sign-in CTAs
            victoryModalOpenCount % 2 === 0 ? (
                <SignInCta />
            ) : (
                <AppDownloadCta />
            )
        ) : (
            <AppDownloadCta />
        );
    }

    // Top priority for web players is getting them on the app
    if (isCandidateForApp()) {
        return ctaForAppCandidates();
    }

    // Top priority for non-app-eligible players on ad-supported platforms is getting them to pro
    if (!pro() && adInterface.adsSupported()) {
        return upgradeToProCta();
    }

    // Player is not a candidate for the app (either already on app or on a laptop),
    // AND player is already pro.
    // Promote sign-in if they are not already signed in.
    return !loginSession?.session ? <SignInCta /> : null;
}
